import React, { useState } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend,
} from 'recharts';
import './SnapshotChart.css';
import { useSnapshots } from '../hooks/useSnapshots';
import { usePerformance } from '../hooks/usePerformance';

const SnapshotChart = () => {
  const [timePeriod, setTimePeriod] = useState('30'); // days
  const [viewMode, setViewMode] = useState('simple'); // 'simple' or 'stacked'
  const [visibleCategories, setVisibleCategories] = useState({
    stocks: true,
    crypto: true,
    preciousMetals: true,
    cash: true,
    realEstate: true,
    liabilities: true,
  });
  const { snapshots, loading, error } = useSnapshots(timePeriod);
  const { performance } = usePerformance(timePeriod);

  const timePeriods = [
    { label: '7D', value: '7' },
    { label: '1M', value: '30' },
    { label: '3M', value: '90' },
    { label: '6M', value: '180' },
    { label: '1Y', value: '365' },
    { label: 'All', value: 'all' },
  ];

  // Toggle category visibility
  const toggleCategory = (key) => {
    setVisibleCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Category configuration for UI
  const categoryConfig = [
    { key: 'stocks', label: '📈 Stocks & ETFs', color: '#3b82f6' },
    { key: 'crypto', label: '₿ Crypto', color: '#f59e0b' },
    { key: 'preciousMetals', label: '🥇 Precious Metals', color: '#a78bfa' },
    { key: 'cash', label: '💵 Cash', color: '#10b981' },
    { key: 'realEstate', label: '🏠 Real Estate', color: '#8b5cf6' },
    { key: 'liabilities', label: '💳 Liabilities', color: '#ef4444' },
  ];

  // Format data for the chart
  const chartData = snapshots
    .sort((a, b) => a.date.localeCompare(b.date)) // Sort by date ascending
    .map((snapshot) => ({
      date: snapshot.date,
      netWorth: snapshot.netWorth || snapshot.totalValue,
      // Add category values for stacked chart
      stocks: snapshot.categories?.stocks?.value || 0,
      crypto: snapshot.categories?.crypto?.value || 0,
      preciousMetals: snapshot.categories?.preciousMetals?.value || 0,
      cash: snapshot.categories?.cash?.value || 0,
      realEstate: snapshot.categories?.realEstate?.value || 0,
      // Liabilities as negative for visual separation
      liabilities: -(Math.abs(snapshot.categories?.liabilities?.value || 0)),
    }));

  // Custom tooltip for simple view
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="label">{`Date: ${label}`}</p>
          <p className="total" style={{ color: '#4F46E5' }}>{`Net Worth: €${data.netWorth.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for stacked view
  const CategoryTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Calculate totals from the payload data
      const assets = payload
        .filter(p => p.dataKey !== 'liabilities')
        .reduce((sum, p) => sum + (p.value || 0), 0);
      const liabilities = Math.abs(payload.find(p => p.dataKey === 'liabilities')?.value || 0);
      const netWorth = assets - liabilities;

      return (
        <div className="custom-tooltip-stacked">
          <p className="label">{`Date: ${label}`}</p>
          <div className="tooltip-section">
            <p className="section-title">Assets:</p>
            {payload
              .filter(p => p.dataKey !== 'liabilities' && p.value > 0)
              .map((entry, index) => (
                <p key={index} style={{ color: entry.color }}>
                  {`${entry.name}: €${entry.value.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}`}
                </p>
              ))}
            <p className="total">Total Assets: €{assets.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}</p>
          </div>
          {liabilities > 0 && (
            <div className="tooltip-section">
              <p className="section-title">Liabilities:</p>
              <p style={{ color: '#ef4444' }}>
                €{liabilities.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          )}
          <div className="tooltip-section net-worth">
            <p className="section-title">Net Worth:</p>
            <p style={{ color: '#4F46E5', fontWeight: 'bold' }}>
              €{netWorth.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Format Y-axis values
  const formatYAxis = (value) => {
    if (value >= 1000) {
      return `€${(value / 1000).toFixed(0)}k`;
    }
    return `€${value}`;
  };

  // Format X-axis dates
  const formatXAxis = (dateString) => {
    const date = new Date(dateString);
    if (timePeriod === '7') {
      // Show day of week for 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (timePeriod === '30' || timePeriod === '90') {
      // Show month/day for 1-3 months
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Show month/year for longer periods
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
  };

  // Calculate Y-axis domain for relative scaling (not from 0)
  const getYAxisDomain = () => {
    if (chartData.length === 0) return [0, 'auto'];

    const values = chartData.map(d => d.netWorth);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;

    // Add 10% padding above and below for better visualization
    const padding = range * 0.1;
    const domainMin = Math.max(0, minValue - padding); // Don't go below 0
    const domainMax = maxValue + padding;

    return [Math.floor(domainMin), Math.ceil(domainMax)];
  };

  if (loading) {
    return (
      <div className="snapshot-chart-container">
        <div className="loading">Loading portfolio history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="snapshot-chart-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="snapshot-chart-container">
        <div className="no-data">
          No snapshot data available. Snapshots are created daily at 11:30 PM.
        </div>
      </div>
    );
  }

  return (
    <div className="snapshot-chart-container">
      <div className="chart-header">
        <h2>Net Worth History</h2>
        <div className="chart-controls">
          <div className="view-mode-toggle">
            <button
              className={`view-btn ${viewMode === 'simple' ? 'active' : ''}`}
              onClick={() => setViewMode('simple')}
            >
              Net Worth
            </button>
            <button
              className={`view-btn ${viewMode === 'stacked' ? 'active' : ''}`}
              onClick={() => setViewMode('stacked')}
            >
              Breakdown
            </button>
          </div>
          <div className="time-period-selector">
            {timePeriods.map((period) => (
              <button
                key={period.value}
                className={`period-btn ${timePeriod === period.value ? 'active' : ''}`}
                onClick={() => setTimePeriod(period.value)}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {performance && (
        <div className="performance-summary">
          <div className="performance-item">
            <span className="performance-label">Change:</span>
            <span
              className={`performance-value ${
                performance.valueChange >= 0 ? 'positive' : 'negative'
              }`}
            >
              {performance.valueChange >= 0 ? '+' : ''}€
              {performance.valueChange.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              ({performance.percentageChange >= 0 ? '+' : ''}
              {performance.percentageChange}%)
            </span>
          </div>
          <div className="performance-item">
            <span className="performance-label">Period:</span>
            <span className="performance-value">
              {performance.startDate} to {performance.endDate}
            </span>
          </div>
        </div>
      )}

      {/* Category toggles - only show in stacked view */}
      {viewMode === 'stacked' && (
        <div className="category-toggles">
          <span className="toggle-label">Show:</span>
          {categoryConfig.map(({ key, label, color }) => {
            // Check if this category has data
            const hasData = chartData.some(d => Math.abs(d[key]) > 0);
            if (!hasData) return null;

            return (
              <label key={key} className="category-toggle">
                <input
                  type="checkbox"
                  checked={visibleCategories[key]}
                  onChange={() => toggleCategory(key)}
                />
                <span
                  className="category-indicator"
                  style={{ backgroundColor: color }}
                />
                <span className="category-name">{label}</span>
              </label>
            );
          })}
        </div>
      )}

      <ResponsiveContainer width="100%" height={400}>
        {viewMode === 'stacked' ? (
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} stackOffset="none">
            <defs>
              <linearGradient id="colorStocks" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="colorCrypto" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="colorPreciousMetals" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="colorCash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="colorRealEstate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="colorLiabilities" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="var(--color-text-tertiary)"
              style={{ fontSize: '12px', fill: 'var(--color-text-tertiary)' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              stroke="var(--color-text-tertiary)"
              style={{ fontSize: '12px', fill: 'var(--color-text-tertiary)' }}
              width={60}
            />
            <Tooltip content={<CategoryTooltip />} />
            <Legend />

            {/* Liabilities (below zero or as negative stack) */}
            {visibleCategories.liabilities && (
              <Area
                type="monotone"
                dataKey="liabilities"
                stackId="1"
                stroke="#ef4444"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorLiabilities)"
                name="Liabilities"
              />
            )}

            {/* Assets (stacked) */}
            {visibleCategories.stocks && (
              <Area
                type="monotone"
                dataKey="stocks"
                stackId="2"
                stroke="#3b82f6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorStocks)"
                name="Stocks & ETFs"
              />
            )}
            {visibleCategories.crypto && (
              <Area
                type="monotone"
                dataKey="crypto"
                stackId="2"
                stroke="#f59e0b"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorCrypto)"
                name="Cryptocurrency"
              />
            )}
            {visibleCategories.preciousMetals && chartData.some(d => d.preciousMetals > 0) && (
              <Area
                type="monotone"
                dataKey="preciousMetals"
                stackId="2"
                stroke="#a78bfa"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorPreciousMetals)"
                name="Precious Metals"
              />
            )}
            {visibleCategories.cash && chartData.some(d => d.cash > 0) && (
              <Area
                type="monotone"
                dataKey="cash"
                stackId="2"
                stroke="#10b981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorCash)"
                name="Cash"
              />
            )}
            {visibleCategories.realEstate && chartData.some(d => d.realEstate > 0) && (
              <Area
                type="monotone"
                dataKey="realEstate"
                stackId="2"
                stroke="#8b5cf6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRealEstate)"
                name="Real Estate & Property"
              />
            )}
          </AreaChart>
        ) : (
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary-500, #8B5CF6)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-primary-500, #8B5CF6)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="var(--color-text-tertiary)"
              style={{ fontSize: '12px', fill: 'var(--color-text-tertiary)' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              stroke="var(--color-text-tertiary)"
              style={{ fontSize: '12px', fill: 'var(--color-text-tertiary)' }}
              width={60}
              domain={getYAxisDomain()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="netWorth"
              stroke="var(--color-primary-500)"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorNetWorth)"
              name="Net Worth"
            />
          </AreaChart>
        )}
      </ResponsiveContainer>

      <div className="snapshot-count">
        Showing {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default SnapshotChart;
